// ─────────────────────────────────────────────────────────────────────────────
// GuideIndex — landing page at /guide
//
// Two responsibilities:
//   1) Brief welcome (this is the documentation, here's how to use it)
//   2) Hub to every other guide page, grouped by section
//
// Adding a new page later: register it in PAGE_GROUPS below and create the
// page component in /pages/. Sidebar nav, route registration, and prev/next
// stay automatic.
// ─────────────────────────────────────────────────────────────────────────────

import { Link } from "react-router-dom";
import { Card } from "../../components/ui/Card";
import { C } from "../../lib/colors";

const PAGE_GROUPS = [
  {
    label: "Start here",
    items: [
      { slug: "welcome",       title: "Welcome to Futures OS", desc: "What the product is, what to expect across your trial." },
    ],
  },
  {
    label: "Daily use",
    items: [
      { slug: "dashboard",     title: "Dashboard",      desc: "Your home screen — today's plan, drawdown room, market context." },
      { slug: "trade-entry",   title: "Trade Entry",    desc: "Log closed trades and the fields that unlock detectors." },
      { slug: "can-i-trade",   title: "Can I Trade?",   desc: "A preflight checklist to run before placing your next order." },
      { slug: "trade-review",  title: "Trade Review",   desc: "Get coaching feedback on a single trade." },
      { slug: "journal",       title: "Journal",        desc: "Browse and edit your full trade history." },
    ],
  },
  {
    label: "Analysis",
    items: [
      { slug: "analytics",     title: "Analytics",      desc: "All nine analytics sub-tabs and how to read a detector card." },
      { slug: "eval-survival", title: "Eval Survival",  desc: "See how your trading history would fare against prop firms' rules." },
    ],
  },
  {
    label: "Setup",
    items: [
      { slug: "portfolio",     title: "Portfolio & Trading Rules", desc: "Add accounts and configure the rules each one trades by." },
      { slug: "strategies",    title: "Strategies",     desc: "Toggle which setups appear in your Trade Entry dropdowns." },
      { slug: "market",        title: "Market",         desc: "Live economic calendar and pre-market brief." },
    ],
  },
  {
    label: "Account",
    items: [
      { slug: "profile",       title: "Profile Settings", desc: "Display name, data export, instrument settings, sign out." },
      { slug: "billing",       title: "Billing",          desc: "Manage your subscription." },
    ],
  },
];

export function GuideIndex() {
  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: 20 }}>
      {/* Header */}
      <header style={{ marginBottom: 28 }}>
        <h1 style={{
          fontSize: 28,
          fontWeight: 800,
          color: C.text,
          margin: "0 0 8px",
          letterSpacing: "-0.02em",
        }}>
          Guide
        </h1>
        <p style={{ fontSize: 14, color: C.muted, margin: 0, lineHeight: 1.6 }}>
          Reference for every feature in Futures OS — what it is, how to use it,
          and how to read what it shows you. Start with <strong style={{ color: C.text }}>Welcome</strong> if
          this is your first time here.
        </p>
      </header>

      {/* Grouped page lists */}
      {PAGE_GROUPS.map((group) => (
        <section key={group.label} style={{ marginBottom: 24 }}>
          <div style={{
            fontSize: 10,
            color: C.muted,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontWeight: 600,
            marginBottom: 10,
            paddingLeft: 4,
          }}>
            {group.label}
          </div>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            {group.items.map((item, idx) => (
              <Link
                key={item.slug}
                to={`/guide/${item.slug}`}
                style={{
                  display: "block",
                  padding: "14px 18px",
                  textDecoration: "none",
                  borderBottom: idx < group.items.length - 1 ? `1px solid ${C.border}40` : "none",
                  transition: "background 0.12s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = C.border + "20")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 12,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 3 }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
                      {item.desc}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, color: C.muted, flexShrink: 0 }}>→</div>
                </div>
              </Link>
            ))}
          </Card>
        </section>
      ))}

      <div style={{ height: 40 }} />
    </div>
  );
}

// Export the page registry so route registration and prev/next can use it
// without duplicating the list.
export { PAGE_GROUPS };
